import { locales, pathnames, REVIEWED_KULULU_LOCALES, REVIEWED_WEDBOX_LOCALES } from "@/config";
import { getRouteLocales } from "@/lib/blogPostLocales";
import { QR_SIGN_LOCALES } from "@/lib/qr-sign-maker/locales";

// Public marketing pages that run PostHog rage and dead click capture
// (PostHogClickCapture), by their route in src/app/[locale]/(site), with the
// locales each one is served in (undefined: all). The match is exact: any
// other path, locale or route added later is off until it is listed here.
// Off on purpose: upload, gallery and live photowall (/upload/...), the /home
// dashboard and everything under it, login, admin, cart, confirmation,
// thank-you, invoice, embedded, error, setarchivesat and dev.
const MARKETING_ROUTES: Readonly<Record<string, readonly string[] | undefined>> = {
  "/": undefined,
  "/blog": undefined,
  "/how-it-works": undefined,
  "/how-to-use-qr-codes": undefined,
  "/sammenligning": undefined,
  // Other locales redirect to /sammenligning.
  "/wedbox": REVIEWED_WEDBOX_LOCALES,
  "/kululu-alternative": REVIEWED_KULULU_LOCALES,
  "/shared-photo-album": undefined,
  // Danish only; other locales 404.
  "/printed-for-you": ["da"],
  "/qr-skilt": QR_SIGN_LOCALES,
  "/photowall": undefined,
  "/julefrokost": undefined,
  "/konfirmation": undefined,
  "/barnedab": undefined,
  "/bar-mitsva": undefined,
  "/fodselsdag": undefined,
  "/privacy-policy": undefined,
  "/terms-and-conditions": undefined,
};

// Blog posts: every /blog/... route in next-intl's pathnames, which lists each
// post in BLOG_POSTS (blog-posts.ts is server only; tests/posthog/clicks-run.mjs
// checks the two match), in the locales BLOG_POST_LOCALES gives it.
const BLOG_POST_ROUTES = Object.keys(pathnames).filter((route) => route.startsWith("/blog/") && !route.includes("["));

function normalize(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

// Per locale, each route under the name next-intl serves in that locale
// (names differ: /en/confirmation is the konfirmation page, /da/confirmation
// the old purchase confirmation).
const marketingPathsByLocale = new Map<string, Set<string>>();
for (const locale of locales) {
  const paths = new Set<string>();
  const routes: [string, readonly string[] | undefined][] = [
    ...Object.entries(MARKETING_ROUTES),
    ...BLOG_POST_ROUTES.map((route): [string, readonly string[] | undefined] => [route, getRouteLocales(route)]),
  ];
  for (const [route, routeLocales] of routes) {
    if (routeLocales && !routeLocales.includes(locale)) continue;
    const localized: unknown = (pathnames as Record<string, unknown>)[route];
    const name = localized && typeof localized === "object"
      ? (localized as Record<string, string | undefined>)[locale] ?? route
      : route;
    paths.add(normalize(name));
  }
  marketingPathsByLocale.set(locale, paths);
}

/** True for a public marketing page: an exact, locale-prefixed, possibly percent-encoded path. */
export function isMarketingPath(pathname: string): boolean {
  let path: string;
  try {
    path = decodeURIComponent(pathname.split(/[?#]/, 1)[0]);
  } catch {
    return false;
  }
  const [locale, ...segments] = normalize(path).split("/").filter(Boolean);
  const paths = locale === undefined ? undefined : marketingPathsByLocale.get(locale);
  return paths?.has("/" + segments.join("/")) ?? false;
}
